/**
 * Utilidades para posicionar Objeto_superior en banners
 */
import type { CSSProperties } from 'react';

export interface ObjectPosition {
  top?: string;
  bottom?: string;
  left?: string;
  right?: string;
  width?: string;
  height?: string;
  transform?: string;
}

/** Posiciones "escalado": contenedor en 3 columnas, imagen en izquierda / centro / derecha */
export const isObjetoSuperiorEscalado = (posicion?: string | null): boolean => {
  const p = posicion?.toLowerCase();
  return p === 'izquierda escalado' || p === 'centrado escalado' || p === 'derecha escalado';
};

/** Estilo del contenedor interno escalado: muy ancho para que object-contain escale por altura (imagen completa visible) */
export const getObjetoSuperiorEscaladoInnerStyle = (posicion?: string | null): CSSProperties => {
  const p = posicion?.toLowerCase();
  const base = { position: 'absolute' as const, top: 0, bottom: 0, width: '100vmax' };
  if (p === 'izquierda escalado') return { ...base, left: 0 };
  if (p === 'derecha escalado') return { ...base, right: 0 };
  if (p === 'centrado escalado') return { ...base, left: '50%', transform: 'translateX(-50%)' };
  return { ...base, left: 0 };
};

/**
 * object-position para la imagen del objeto superior en mobile:
 * alinea el contenido de la imagen al mismo borde que la posición (ej. derecha = pegado a la derecha como en desktop)
 */
export const getObjetoSuperiorObjectPosition = (posicion?: string | null): string => {
  if (!posicion) return 'right bottom';
  const p = posicion.toLowerCase();
  if (p === 'izquierda escalado') return 'left center';
  if (p === 'centrado escalado') return 'center';
  if (p === 'derecha escalado') return 'right center';
  if (p.includes('superior') && p.includes('derecha')) return 'right top';
  if (p.includes('superior') && p.includes('izquierda')) return 'left top';
  if (p.includes('superior')) return 'center top';
  if (p.includes('inferior') && p.includes('derecha')) return 'right bottom';
  if (p.includes('inferior') && p.includes('izquierda')) return 'left bottom';
  if (p.includes('inferior')) return 'center bottom';
  return 'right bottom';
};

/**
 * Obtiene la posición del objeto superior según la configuración de Strapi (Desktop)
 * Los bordes de la imagen deben coincidir con los bordes del contenedor (sin padding)
 * @param posicion - "superior izquierda" | "superior centrado" | "superior derecha" | "inferior izquierda" | "inferior centrado" | "inferior derecha"
 * @returns Objeto con estilos CSS para posición alineada con los bordes del contenedor
 */
export const getObjetoSuperiorPosition = (posicion?: string | null): ObjectPosition => {
  const p = posicion?.toLowerCase();
  switch (p) {
    case 'izquierda escalado':
      return { top: '0', bottom: '0', left: '0', width: '33.333%' };
    case 'centrado escalado':
      return { top: '0', bottom: '0', left: '33.333%', width: '33.333%' };
    case 'derecha escalado':
      return { top: '0', bottom: '0', right: '0', width: '33.333%' };
    case 'superior izquierda':
      return {
        top: '0px',
        left: '0px',
      };
    case 'superior centrado':
      return {
        top: '0px',
        left: '50%',
        transform: 'translateX(-50%)',
      };
    case 'superior derecha':
      return {
        top: '0px',
        right: '0px',
      };
    case 'inferior izquierda':
      return {
        bottom: '0px',
        left: '0px',
      };
    case 'inferior centrado':
      return {
        bottom: '0px',
        left: '50%',
        transform: 'translateX(-50%)',
      };
    case 'inferior derecha':
      return {
        bottom: '0px',
        right: '0px',
      };
    default:
      return {
        bottom: '0px',
        right: '0px',
      };
  }
};

/**
 * Obtiene la posición del objeto superior para mobile según la configuración de Strapi
 * Los bordes de la imagen deben coincidir con los bordes del contenedor (sin padding)
 * @param posicion - "superior izquierda" | "superior centrado" | "superior derecha" | "inferior izquierda" | "inferior centrado" | "inferior derecha"
 * @returns Objeto con estilos CSS para posición alineada con los bordes del contenedor
 */
export const getObjetoSuperiorPositionMobile = (posicion?: string | null): ObjectPosition => {
  const p = posicion?.toLowerCase();
  switch (p) {
    case 'izquierda escalado':
      return { top: '0', bottom: '0', left: '0', width: '33.333%' };
    case 'centrado escalado':
      return { top: '0', bottom: '0', left: '33.333%', width: '33.333%' };
    case 'derecha escalado':
      return { top: '0', bottom: '0', right: '0', width: '33.333%' };
    case 'superior izquierda':
      return {
        top: '0px',
        left: '0px',
      };
    case 'superior centrado':
      return {
        top: '0px',
        left: '50%',
        transform: 'translateX(-50%)',
      };
    case 'superior derecha':
      return {
        top: '0px',
        right: '0px',
      };
    case 'inferior izquierda':
      return {
        bottom: '0px',
        left: '0px',
      };
    case 'inferior centrado':
      return {
        bottom: '0px',
        left: '50%',
        transform: 'translateX(-50%)',
      };
    case 'inferior derecha':
      return {
        bottom: '0px',
        right: '0px',
      };
    default:
      return {
        bottom: '0px',
        right: '0px',
      };
  }
};

/**
 * Obtiene el estilo de ajuste para Objeto_fondo
 * @param ajuste - "sangrado" | "ajustado" | "alineacion izquierda" | "alineacion central" | "alineacion derecha"
 * @returns Clase CSS o estilo para object-fit
 */
export const getObjetoFondoAjuste = (ajuste?: string | null): string => {
  const a = ajuste?.toLowerCase();
  switch (a) {
    case 'sangrado':
      return 'object-cover';
    case 'ajustado':
      return 'object-contain';
    case 'alineacion izquierda':
    case 'alineacion central':
    case 'alineacion derecha':
      return 'object-contain'; // imagen completa visible, alineada según el valor (izquierda/centro/derecha)
    default:
      return 'object-cover';
  }
};

/**
 * object-position del Objeto_fondo: si el ajuste es una alineación (izquierda/central/derecha), la usa; si no, usa posicion.
 */
export const getObjetoFondoPositionResolved = (ajuste?: string | null, posicion?: string | null): string => {
  const a = ajuste?.toLowerCase();
  if (a === 'alineacion izquierda') return 'left center';
  if (a === 'alineacion central') return 'center';
  if (a === 'alineacion derecha') return 'right center';
  return getObjetoFondoPosition(posicion);
};

/**
 * Obtiene el object-position CSS para alinear la imagen de fondo con los bordes del contenedor
 * @param posicion - "superior izquierda" | "superior centrado" | "superior derecha" | "inferior izquierda" | "inferior centrado" | "inferior derecha" | "centro izquierda" | "centro" | "centro derecha"
 * @returns Valor de object-position CSS
 */
export const getObjetoFondoPosition = (posicion?: string | null): string => {
  if (!posicion) {
    return 'center'; // Por defecto centrado
  }

  switch (posicion.toLowerCase()) {
    case 'superior izquierda':
      return 'top left';
    case 'superior centrado':
    case 'superior centro':
      return 'top center';
    case 'superior derecha':
      return 'top right';
    case 'inferior izquierda':
      return 'bottom left';
    case 'inferior centrado':
    case 'inferior centro':
      return 'bottom center';
    case 'inferior derecha':
      return 'bottom right';
    case 'centro izquierda':
    case 'centro izquierdo':
      return 'center left';
    case 'centro':
    case 'centro centrado':
      return 'center';
    case 'centro derecha':
      return 'center right';
    default:
      return 'center';
  }
};

